// components/SideBar.tsx
import { loadTree } from 'core/MenuTreeHelper.js';
import { withRouter } from 'core/withRouter';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from 'services/auth.service';
import pageStateService from 'core/PageState.service';
import receptionService from 'services/reception.service';
import { getMenus } from 'models/admin';

class Params {
    path: string = '';
}

type MenuCounts = {
    inboundCnt: number;
    repairCnt: number;
    outboundCnt: number;
    tripCnt: number;
    arrearsCnt?: number;
};

function SideBar(props: any) {
    const navigate = useNavigate();
    const location = useLocation();
    const [params, setParams] = useState(new Params());
    const [menuPermissions, setMenuPermissions] = useState<string[]>([]);

    const [counts, setCounts] = useState<MenuCounts>({
        inboundCnt: 0,
        repairCnt: 0,
        outboundCnt: 0,
        tripCnt: 0,
        arrearsCnt: 0,
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const redirect = searchParams.get('r');

        const token = authService.getToken();
        if (!token) {
            if (redirect && redirect.startsWith('/find')) {
                navigate(redirect, { replace: true });
                return;
            }
            navigate('/login?r=' + location.pathname);
            return;
        }

        // 토큰에서 메뉴 권한 정보 추출
        const permissions = token?.info?.menus || [];
        const roles = token.roles || [];

        // master 권한은 모든 메뉴 접근 가능
        if (roles.includes('master')) {
            setMenuPermissions(getMenus().map(menu => menu.path));
        } else {
            setMenuPermissions(permissions);
        }

        setParams(() => ({
            ...params,
            path: location.pathname,
        }));

        loadTree();
        getMenuCount();

        if (token && redirect) {
            setTimeout(() => {
                navigate(redirect, { replace: true });
            }, 300);
        }
    }, []);

    useEffect(() => {
        setParams(() => ({
            ...params,
            path: location.pathname,
        }));
        getMenuCount();
    }, [location]);

    async function goPage(path: string) {
        await pageStateService.clearPageState();
        navigate(path);
    }

    async function getMenuCount() {
        try {
            const res = await receptionService.getMenuCount();
            setCounts({
                inboundCnt: Number(res?.inboundCnt ?? 0),
                repairCnt: Number(res?.repairCnt ?? 0),
                outboundCnt: Number(res?.outboundCnt ?? 0),
                tripCnt: Number(res?.tripCnt ?? 0),
                arrearsCnt: Number((res as any)?.arrearsCnt ?? 0),
            });
        } catch (e) {
            console.error('getMenuCount failed:', e);
            setCounts((prev) => ({ ...prev }));
        }
    }

    // 메뉴 접근 권한 확인
    const hasMenu = (menuPath: string): boolean => {
        return menuPermissions.includes(menuPath);
    };

    const renderBadge = (n: number | undefined, title?: string, showZero = true) => {
        const val = Number(n ?? 0);
        const cls = val > 0 ? 'badge-primary' : 'badge-secondary';
        return (
            (showZero || val > 0) && (
                <span className={`right badge ${cls}`} title={title}>
                    {val}
                </span>
            )
        );
    };

    return (
        <aside className="main-sidebar sidebar-light-primary elevation-4">
            <a onClick={() => goPage('/')} className="brand-link">
                <span className="brand-text">수상한 난방</span>
                <span className="brand-logo"></span>
            </a>

            <div className="sidebar">
                <nav className="mt-3">
                    <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu" data-accordion="true">
                        {/* 홈 */}
                        {hasMenu('/') && (
                            <li className={`nav-item ${params.path == '/' ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/')}>
                                    <i className="nav-icon fas fa-home"></i>
                                    <p>홈</p>
                                </a>
                            </li>
                        )}

                        {/* 접수 관리 */}
                        {hasMenu('/reception') && (
                            <li className={`nav-item ${params.path.startsWith('/reception') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/reception')}>
                                    <i className="nav-icon fas fa-clipboard-list"></i>
                                    <p>접수 관리</p>
                                </a>
                            </li>
                        )}

                        {/* 오늘 접수 */}
                        {hasMenu('/today-reception') && (
                            <li className={`nav-item ${params.path.startsWith('/today-reception') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/today-reception')}>
                                    <i className="nav-icon fas fa-clipboard-list"></i>
                                    <p>TODAY접수</p>
                                </a>
                            </li>
                        )}

                        {/* 입고 관리 */}
                        {hasMenu('/inbound') && (
                            <li className={`nav-item ${params.path.startsWith('/inbound') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/inbound')}>
                                    <i className="nav-icon fas fa-boxes"></i>
                                    <p>
                                        입고 관리
                                        {renderBadge(counts.inboundCnt, '입고예정')}
                                    </p>
                                </a>
                            </li>
                        )}

                        {/* 수리 관리 */}
                        {hasMenu('/repair') && (
                            <li className={`nav-item ${params.path.startsWith('/repair') && !params.path.startsWith('/repair-statistics') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/repair')}>
                                    <i className="nav-icon fas fa-tools"></i>
                                    <p>
                                        수리 관리
                                        {renderBadge(counts.repairCnt, '수리중')}
                                    </p>
                                </a>
                            </li>
                        )}

                        {/* 출고 관리 */}
                        {hasMenu('/outbound') && (
                            <li className={`nav-item ${params.path.startsWith('/outbound') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/outbound')}>
                                    <i className="nav-icon fas fa-truck"></i>
                                    <p>
                                        출고 관리
                                        {renderBadge(counts.outboundCnt, '출고대기')}
                                    </p>
                                </a>
                            </li>
                        )}

                        {/* 출장 관리 */}
                        {hasMenu('/trip') && (
                            <li className={`nav-item ${params.path.startsWith('/trip') && !params.path.startsWith('/trip-fee') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/trip')}>
                                    <i className="nav-icon fas fa-route"></i>
                                    <p>
                                        출장 관리
                                        {renderBadge(counts.tripCnt, '출장대기')}
                                    </p>
                                </a>
                            </li>
                        )}

                        {/* 견적 관리 */}
                        {hasMenu('/estimate') && (
                            <li className={`nav-item ${params.path.startsWith('/estimate') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/estimate')}>
                                    <i className="nav-icon fas fa-file-invoice"></i>
                                    <p>
                                        견적 관리
                                        {renderBadge(counts.arrearsCnt, '견적')}
                                    </p>
                                </a>
                            </li>
                        )}

                        {/* 맞교환 관리 */}
                        {hasMenu('/exchange') && (
                            <li className={`nav-item ${params.path.startsWith('/exchange') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/exchange')}>
                                    <i className="nav-icon fas fa-file-invoice"></i>
                                    <p>맞교환 관리</p>
                                </a>
                            </li>
                        )}

                        {/* 보일러 재고현황 */}
                        {hasMenu('/stock') && (
                            <li className={`nav-item ${params.path.startsWith('/stock') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/stock')}>
                                    <i className="nav-icon fas fa-warehouse"></i>
                                    <p>보일러 재고현황</p>
                                </a>
                            </li>
                        )}

                        {/* AS현황 분석 */}
                        {hasMenu('/as-analytics') && (
                            <li className={`nav-item ${params.path.startsWith('/as-analytics') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/as-analytics')}>
                                    <i className="nav-icon fas fa-chart-line"></i>
                                    <p>AS현황 분석</p>
                                </a>
                            </li>
                        )}

                        {/* AS 수익분석 조회 */}
                        {hasMenu('/repair-statistics') && (
                            <li className={`nav-item ${params.path.startsWith('/repair-statistics') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/repair-statistics')}>
                                    <i className="nav-icon fas fa-chart-bar"></i>
                                    <p>AS 수익분석</p>
                                </a>
                            </li>
                        )}

                        {/* 출장자별 통계 */}
                        {hasMenu('/agent-stats') && (
                            <li className={`nav-item ${params.path.startsWith('/agent-stats') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/agent-stats')}>
                                    <i className="nav-icon fas fas fa-users-cog"></i>
                                    <p>출장자별 통계</p>
                                </a>
                            </li>
                        )}

                        {/* 캘린더 */}
                        {hasMenu('/as-calendar') && (
                            <li className={`nav-item ${params.path.startsWith('/as-calendar') ? 'menu-is-opening menu-open' : ''}`}>
                                <a className="nav-link" onClick={() => goPage('/as-calendar')}>
                                    <i className="nav-icon fas fa-calendar-alt"></i>
                                    <p>캘린더</p>
                                </a>
                            </li>
                        )}
                    </ul>
                </nav>
            </div>
        </aside>
    );
}

export default withRouter(SideBar);